import { useSyncExternalStore } from "react";

/**
 * The last users this admin opened from a search — the Users page's landing
 * list before a query is typed, and a "Recent" group in the ⌘K palette.
 *
 * Kept in localStorage (per browser, per admin machine; no server state):
 * newest first, de-duplicated by id, capped at RECENT_USERS_LIMIT. Only the
 * identity needed to render a row is stored (id, email, name) — never a
 * balance or status, which would go stale and is customer data this
 * browser has no business keeping around.
 */
export interface RecentUser {
  id: string;
  email: string;
  name: string | null;
  /** Epoch ms of the last open. */
  openedAt: number;
}

export const RECENT_USERS_LIMIT = 10;
const STORAGE_KEY = "moreta.admin.recentUsers";
const CHANGE_EVENT = "moreta:recent-users";

const EMPTY: RecentUser[] = [];
let cache: { raw: string | null; parsed: RecentUser[] } = {
  raw: null,
  parsed: EMPTY,
};

const isRecentUser = (value: unknown): value is RecentUser =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as RecentUser).id === "string" &&
  typeof (value as RecentUser).email === "string" &&
  typeof (value as RecentUser).openedAt === "number";

const readRaw = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

const read = (): RecentUser[] => {
  const raw = readRaw();
  // useSyncExternalStore needs a referentially stable snapshot while the
  // stored value is unchanged, so parse once per distinct raw string.
  if (raw === cache.raw) return cache.parsed;
  let parsed: RecentUser[] = EMPTY;
  if (raw) {
    try {
      const value: unknown = JSON.parse(raw);
      parsed = Array.isArray(value) ? value.filter(isRecentUser) : EMPTY;
    } catch {
      parsed = EMPTY;
    }
  }
  cache = { raw, parsed };
  return parsed;
};

const write = (list: RecentUser[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // Storage full or disabled — the list is a convenience, not state.
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
};

export const rememberRecentUser = (user: {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
}): void => {
  const name =
    [user.firstName, user.lastName].filter(Boolean).join(" ") || null;
  const next: RecentUser[] = [
    { id: user.id, email: user.email, name, openedAt: Date.now() },
    ...read().filter((entry) => entry.id !== user.id),
  ].slice(0, RECENT_USERS_LIMIT);
  write(next);
};

export const forgetRecentUser = (id: string): void => {
  write(read().filter((entry) => entry.id !== id));
};

export const clearRecentUsers = (): void => {
  write([]);
};

const subscribe = (onChange: () => void) => {
  window.addEventListener(CHANGE_EVENT, onChange);
  // Another tab opening a user updates this one too.
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
};

export const useRecentUsers = (): RecentUser[] =>
  useSyncExternalStore(subscribe, read, () => EMPTY);
